import { DataCard } from '@fluentsms/agentnet-web-components';
import { Box, createStyles, makeStyles, Theme, Tooltip, Typography } from '@material-ui/core';
import { CloudUploadOutlined, DeleteOutlined } from '@material-ui/icons';
import { FC } from 'react';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import BottomAppBar from 'ui-kit/components/bottomAppBar/BottomAppBar';
import { AgentNetCheckBox } from 'ui-kit/inputs/Checkbox';
import { TitleMonitoringConfiguration as TitleMonitoringConfigurationState } from './useTitleMonitoringConfiguration';

interface TitleMonitoringConfigurationProps {
  configuration: TitleMonitoringConfigurationState;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dataCard: {
      height: 'fit-content',
    },
    logoHeading: {
      marginBottom: theme.spacing(0.5),
    },
    logoDescription: {
      marginBottom: theme.spacing(1),
    },
    logoSpec: {
      marginBottom: theme.spacing(0.25),
    },
    logoSpecLast: {
      marginBottom: theme.spacing(2),
    },
    logoSpecLabel: {
      fontWeight: 600,
    },
    logoPreview: {
      display: 'block',
      maxHeight: 85,
      marginBottom: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
    },
    logoActions: {
      display: 'flex',
      gap: theme.spacing(1),
    },
    enrollmentCard: {
      height: 'fit-content',
      marginTop: theme.spacing(3),
    },
    enrollmentDescription: {
      marginBottom: theme.spacing(2),
    },
    optionRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(2),
    },
    optionRowSpacing: {
      marginBottom: theme.spacing(2),
    },
    optionTitle: {
      marginBottom: theme.spacing(0.5),
    },
    optionAction: {
      flexShrink: 0,
    },
  }),
);

const TitleMonitoringConfiguration: FC<TitleMonitoringConfigurationProps> = ({ configuration }) => {
  const classes = useStyles();
  const {
    logoUrl,
    includeCoverPage,
    setIncludeCoverPage,
    fileInputRef,
    handleLogoUpload,
    handleLogoDelete,
    handleSave,
    handleCancel,
    isDirty,
    isSaving,
    downloadFlyer,
    isFlyerLoading,
    canDownloadFlyer,
  } = configuration;

  return (
    <>
      <DataCard heading="Co-Branding" className={classes.dataCard}>
        <Box p={3}>
          <Typography variant="h4" className={classes.logoHeading}>
            Company Logo
          </Typography>
          <Typography variant="body1" color="textSecondary" className={classes.logoDescription}>
            Upload your logo to co-brand the title monitoring experience for your buyers. The logo will automatically be
            resized to fit. If you don&apos;t upload a logo, the First American logo will be used instead.
          </Typography>
          <Typography variant="body2" className={classes.logoSpec}>
            <span className={classes.logoSpecLabel}>Recommended Size:</span> Height of 85px
          </Typography>
          <Typography variant="body2" className={classes.logoSpecLast}>
            <span className={classes.logoSpecLabel}>Accepted File Formats:</span> JPG, PNG
          </Typography>

          <input
            ref={fileInputRef}
            type="file"
            accept=".jpg,.jpeg,.png"
            style={{ display: 'none' }}
            onChange={handleLogoUpload}
            data-testid="title-monitoring-logo-input"
          />

          {logoUrl ? (
            <Box>
              <img src={logoUrl} alt="Company logo" className={classes.logoPreview} />
              <Box className={classes.logoActions}>
                <AgentNetButton
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<CloudUploadOutlined />}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="title-monitoring-logo-replace"
                >
                  Replace
                </AgentNetButton>
                <AgentNetButton
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<DeleteOutlined />}
                  onClick={handleLogoDelete}
                  data-testid="title-monitoring-logo-delete"
                >
                  Delete
                </AgentNetButton>
              </Box>
            </Box>
          ) : (
            <AgentNetButton
              variant="contained"
              color="primary"
              size="small"
              startIcon={<CloudUploadOutlined />}
              onClick={() => fileInputRef.current?.click()}
              data-testid="title-monitoring-logo-upload"
            >
              Upload
            </AgentNetButton>
          )}
        </Box>
      </DataCard>

      <DataCard heading="Enrollment Options" className={classes.enrollmentCard}>
        <Box p={3}>
          <Typography variant="body1" color="textSecondary" className={classes.enrollmentDescription}>
            Choose how buyers can enroll in Title Monitoring. Each option includes your company branding and provides
            buyers with a QR code to begin enrollment.
          </Typography>

          <Box className={`${classes.optionRow} ${classes.optionRowSpacing}`}>
            <Box>
              <Typography variant="h4" className={classes.optionTitle}>
                Marketing Flyer
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Download a co-branded flyer that can be shared with buyers at any point during the transaction. Buyers
                scan the QR code and enter their property address, name, and email to enroll. Property eligibility is
                checked during enrollment.
              </Typography>
            </Box>
            <Tooltip title={isDirty ? 'Save your changes to download the flyer.' : ''} arrow placement="top">
              <div className={classes.optionAction}>
                <AgentNetButton
                  variant="contained"
                  color="primary"
                  size="small"
                  data-testid="title-monitoring-download-flyer"
                  onClick={downloadFlyer}
                  loading={isFlyerLoading}
                  disabled={isFlyerLoading || !canDownloadFlyer}
                >
                  Download Flyer
                </AgentNetButton>
              </div>
            </Tooltip>
          </Box>

          <Box className={classes.optionRow}>
            <Box>
              <Typography variant="h4" className={classes.optionTitle}>
                Owner&apos;s Policy Insert
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Automatically include a co-branded enrollment insert with eligible owner&apos;s policies. Because the
                property has already been verified, buyers only need to provide their name and email to enroll. The
                insert is included only for properties eligible for Title Monitoring.
              </Typography>
            </Box>
            <Box className={classes.optionAction}>
              <AgentNetCheckBox
                label=""
                value={includeCoverPage}
                checkHandler={(checked) => setIncludeCoverPage(checked)}
                data-testid="title-monitoring-include-cover-page"
              />
            </Box>
          </Box>
        </Box>
      </DataCard>

      {isDirty && (
        <BottomAppBar
          primaryActionLabel="Save Changes"
          onPrimaryClick={handleSave}
          primaryButtonLoader={isSaving}
          disablePrimary={isSaving}
          cancelLabel="Cancel"
          onCancel={handleCancel}
          primaryActionDataQa="title-monitoring-cobranding-save"
          cancelDataQa="title-monitoring-cobranding-cancel"
        />
      )}
    </>
  );
};

export default TitleMonitoringConfiguration;
